// frontend/src/components/AddClientModal.jsx
// Form to create a new client and generate their loyalty card.

import { useState } from 'react';
import { api } from '../utils/api';
import { useLanguage } from '../utils/i18n';
import { validateField } from '../utils/validation';
import { useNotification } from '../utils/NotificationContext';
import { useAuth } from '../utils/AuthContext';
const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

export default function AddClientModal({ businessId, onClose, onSuccess }) {
  const { t } = useLanguage();
  const { showNotification } = useNotification();
  const { isDemo } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  const onDemoClick = (e) => {
    if (isDemo) {
      e.preventDefault();
      showNotification(t('demo_feature_restricted'), 'info');
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Regex Validations
    if (!validateField('name', name, true)) {
      setError(t('err_valid_name'));
      setLoading(false);
      return;
    }
    if (email && !validateField('email', email)) {
      setError(t('err_valid_email'));
      setLoading(false);
      return;
    }
    if (phone && !validateField('phone', phone)) {
      setError(t('err_valid_phone'));
      setLoading(false);
      return;
    }
    try {
      const data = await api.createClientCard({ businessId, clientName: name, clientEmail: email, clientPhone: phone });
      setCreated(data);
      showNotification(`${t('card_created')} - ${name}`, 'success');
      onSuccess?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal glass-card" style={{ maxWidth: 460, position: 'relative', overflow: 'hidden' }}>
        {/* Decorative Background Shapes */}
        <div className="bg-shape shape-1" style={{ position: 'absolute', top: '-10%', right: '-10%', width: 200, height: 200, background: 'var(--primary)', filter: 'blur(60px)', opacity: 0.1, borderRadius: '50%', zIndex: 0 }}></div>
        <div className="bg-shape shape-2" style={{ position: 'absolute', bottom: '-10%', left: '-10%', width: 250, height: 250, background: 'var(--accent)', filter: 'blur(80px)', opacity: 0.15, borderRadius: '50%', zIndex: 0 }}></div>

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="modal-header">
            <h2 style={{ fontSize: 24, fontWeight: 800 }}>{created ? t('card_created') : t('add_new_client')}</h2>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>

          {!created ? (
            <>
              {error && <div className="alert alert-error">{error}</div>}
              <form onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label>{t('client_name')}</label>
                  <input value={name} onChange={e => setName(e.target.value)} placeholder="John Doe" required />
                </div>
                <div className="form-group">
                  <label>{t('email')} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>{t('optional')}</span></label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@example.com" />
                </div>
                <div className="form-group">
                  <label>{t('phone')} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>{t('optional')}</span></label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 555 0100" />
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button type="button" className="btn btn-outline" onClick={onClose} style={{ flex: 1, justifyContent: 'center' }}>{t('cancel')}</button>
                  <button type="submit" className="btn btn-primary btn-glow" disabled={loading} style={{ flex: 1, justifyContent: 'center' }}>
                    {loading ? t('creating') : t('create_card')}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <p style={{ marginBottom: 20, color: 'var(--text)', fontSize: 16 }}>
                {t('share_with')} <strong>{created.client.name}</strong>.
              </p>
              {/* QR Code image */}
              {created.card.qrCode && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ background: '#fff', padding: 16, borderRadius: 20, display: 'inline-block' }}>
                    <img src={created.card.qrCode} alt="Client QR Code" style={{ width: 180, height: 180, display: 'block' }} />
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 12, fontWeight: 500 }}>{t('client_registration_qr')}</p>
                </div>
              )}
              {/* Wallet links */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                {created.card.wallet_link && (
                  <a
                    href={isDemo ? '#' : created.card.wallet_link}
                    onClick={onDemoClick}
                    target="_blank"
                    rel="noreferrer"
                    className="btn hover-lift"
                    style={{ background: '#fff', color: '#000', border: '1px solid #ddd', width: '100%', justifyContent: 'center', padding: '12px 20px', fontSize: 15, gap: 8, textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20"><path fill="#4285F4" d="M21.2 8.9l-1.7-3a.5.5 0 0 0-.7-.2l-1.7 1a.5.5 0 0 1-.7-.2L14.7 3.5a.5.5 0 0 0-.7-.2L12.3 4.3a.5.5 0 0 1-.7-.2L10.9 2.4a.5.5 0 0 0-.4-.3.5.5 0 0 0-.4.1L2.8 8.3a.5.5 0 0 0-.1.7l1.7 3a.5.5 0 0 0 .7.2L12 8l4 6.9" /><path fill="#EA4335" d="M16 14.9l-4-6.9-6.9 4a.5.5 0 0 1-.7-.2L2.8 9a.5.5 0 0 0-.1.7l5.1 8.8a.5.5 0 0 0 .7.2l1.7-1a.5.5 0 0 1 .7.2l1.7 3a.5.5 0 0 0 .7.2l1.7-1a.5.5 0 0 1 .7.2l.7 1.2a.5.5 0 0 0 .7.2l1.7-1a.5.5 0 0 0 .2-.7L16 14.9z" /></svg>
                    {t('add_to_wallet')}
                  </a>
                )}

                <a
                  href={isDemo ? '#' : `${BACKEND_URL}/apple-pass/${created.card.wallet_object_id}`}
                  onClick={onDemoClick}
                  target="_blank"
                  rel="noreferrer"
                  className="btn hover-lift"
                  style={{ background: '#000', color: '#fff', border: '1px solid #000', width: '100%', justifyContent: 'center', padding: '12px 20px', fontSize: 15, gap: 8, textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                >
                  <svg viewBox="0 0 384 512" width="18" height="18" fill="currentColor">
                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                  </svg>
                  {t('add_to_apple_wallet')}
                </a>
              </div>
              <button className="btn btn-outline" onClick={onClose} style={{ width: '100%', justifyContent: 'center' }}>{t('close')}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
